import { useEffect, useState } from 'react';
import Reveal from '../../components/Reveal';
import HeroSlideshow from '../../components/HeroSlideshow';
import SafeImage from '../../components/SafeImage';
import './zanzibar.css';
import {
  IMAGE_FALLBACK,
  ZANZIBAR_HERO_SLIDES,
  ZANZIBAR_PAGE_IMAGES,
} from '../../data/destinationImages';

const ZANZIBAR_IMAGES = ZANZIBAR_PAGE_IMAGES;

const HERO_STATS = [
  { value: 'UNESCO', label: 'Stone Town heritage', delay: 0 },
  { value: '25+', label: 'White-sand beaches', delay: 80 },
  { value: '50+', label: 'Spice varieties', delay: 160 },
  { value: '20 min', label: 'Flight from Dar', delay: 240 },
];

const HIGHLIGHTS = [
  {
    iconImage: ZANZIBAR_IMAGES.highlightStonetown,
    title: 'Stone Town',
    text: 'Wander labyrinthine alleys, carved doorways, and the House of Wonders a living UNESCO World Heritage city.',
    facePosition: 'center 40%',
  },
  {
    iconImage: ZANZIBAR_IMAGES.highlightSpices,
    title: 'Spice Tours',
    text: 'Walk fragrant clove, vanilla, and cinnamon farms Zanzibar earned its name as the Spice Islands of the Indian Ocean.',
    facePosition: 'center 45%',
  },
  {
    iconImage: ZANZIBAR_IMAGES.highlightBeach,
    title: 'Pristine Beaches',
    text: 'Powder-white sands at Nungwi, Kendwa, and Paje turquoise water, palm shade, and unhurried island time.',
    facePosition: 'center 35%',
  },
  {
    iconImage: ZANZIBAR_IMAGES.highlightReef,
    title: 'Reefs & Snorkeling',
    text: 'Mnemba Atoll and coral gardens teem with tropical fish clear warm water ideal for snorkeling and diving.',
    facePosition: 'center 38%',
  },
  {
    iconImage: ZANZIBAR_IMAGES.highlightForest,
    title: 'Jozani Forest',
    text: 'Home to rare red colobus monkeys and mangrove boardwalks Zanzibar\'s only national park on the main island.',
    facePosition: 'center 30%',
  },
  {
    iconImage: ZANZIBAR_IMAGES.highlightDhow,
    title: 'Dhow Sunsets',
    text: 'Sail a traditional wooden dhow at golden hour the classic Zanzibar finale after a day on the water.',
    facePosition: 'center 42%',
  },
];

const SEASONS = [
  {
    badge: 'peak',
    title: 'Dry Season',
    months: 'June – October',
    text: 'Sunny skies, calm seas, and the kaskazi wind peak beach weather and ideal conditions for diving and dhow trips.',
  },
  {
    badge: 'green',
    title: 'Short Rains',
    months: 'November – December',
    text: 'Warm temperatures with brief afternoon showers. Lush spice farms and fewer visitors on the east-coast beaches.',
  },
  {
    badge: 'shoulder',
    title: 'Long Rains',
    months: 'March – May',
    text: 'Heavier rains but still warm. Lower lodge rates and dramatic skies mornings are often clear and bright.',
  },
];

const EXPERIENCES = [
  {
    image: ZANZIBAR_IMAGES.experienceStonetown,
    title: 'Stone Town Heritage Walk',
    text: 'Explore Forodhani Gardens, the Old Fort, and Swahili architecture with a local guide who brings centuries of trade history to life.',
  },
  {
    image: ZANZIBAR_IMAGES.experienceSpices,
    title: 'Spice Farm Experience',
    text: 'Touch, smell, and taste cloves, nutmeg, and cardamom on a working farm then enjoy a traditional Swahili lunch.',
  },
  {
    image: ZANZIBAR_IMAGES.beach,
    title: 'Beach & Reef Days',
    text: 'Split your stay between north-coast resorts and east-coast kite beaches with snorkeling trips to Mnemba or Sandbank.',
  },
];

const GALLERY_IMAGES = [
  { src: ZANZIBAR_IMAGES.hero, caption: 'Zanzibar Coastline' },
  { src: ZANZIBAR_IMAGES.beach, caption: 'White-Sand Beach' },
  { src: ZANZIBAR_IMAGES.galleryStonetown, caption: 'Stone Town' },
  { src: ZANZIBAR_IMAGES.gallerySpices, caption: 'Spice Island' },
  { src: ZANZIBAR_IMAGES.coast, caption: 'Turquoise Waters' },
  { src: ZANZIBAR_IMAGES.galleryDhow, caption: 'Dhow at Sunset' },
];

const SAFARI_PACKAGES = [
  {
    image: ZANZIBAR_IMAGES.packageStonetown,
    title: 'Stone Town Culture Escape',
    duration: '2–3 days',
    tag: 'City break',
    text: 'Stay in a Stone Town boutique hotel heritage walks, Forodhani night market, and a spice farm visit at an easy island pace.',
    highlights: ['Stone Town hotel', 'Guided heritage walk', 'Spice farm tour'],
  },
  {
    image: ZANZIBAR_IMAGES.beach,
    title: 'Beach Retreat',
    duration: '4–5 days',
    tag: 'Beach focus',
    text: 'Unwind on Nungwi, Kendwa, or Paje powder sand, reef snorkeling, and sunsets with nothing on the agenda but the ocean.',
    highlights: ['Beach lodge', 'Snorkeling trip', 'Leisure days'],
  },
  {
    image: ZANZIBAR_IMAGES.packageCombo,
    title: 'Stone Town & Beach Combo',
    duration: '5–6 days',
    tag: 'Classic combo',
    text: 'Split your stay between historic Stone Town and a north or east-coast beach culture by day, turquoise water by night.',
    highlights: ['City + beach', 'Dhow sunset sail', 'Flexible lodges'],
  },
  {
    image: ZANZIBAR_IMAGES.coast,
    title: 'Safari & Sea Finale',
    duration: '8–12 days',
    tag: 'Most popular',
    text: 'Pair a mainland safari with Zanzibar beach days the classic Tanzania journey from wildlife to Indian Ocean shores.',
    highlights: ['Safari + island', 'Private transfers', 'Tailored lodges'],
  },
];

const GETTING_THERE_ROUTES = [
  'Fly from Dar es Salaam to Abeid Amani Karume International Airport (ZNZ) approx. 20 minutes',
  'Take the fast ferry from Dar es Salaam to Stone Town approx. 2 hours across the Zanzibar Channel',
  'Combine with a mainland safari fly Kilimanjaro (JRO) to Zanzibar after your Northern Circuit trek',
  'Most itineraries split time between Stone Town culture and north or east-coast beach lodges',
];

function Lightbox({ image, onClose }) {
  if (!image) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="zanzibar-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={handleBackdropClick}
    >
      <button
        className="zanzibar-lightbox__close"
        aria-label="Close image viewer"
        onClick={onClose}
      >
        ✕
      </button>
      <img
        className="zanzibar-lightbox__img"
        src={image.src}
        alt={image.caption}
      />
      <span className="zanzibar-lightbox__caption">{image.caption}</span>
    </div>
  );
}

function Zanzibar() {
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="zanzibar">
      <section className="zanzibar-hero" aria-labelledby="zanzibar-hero-heading">
        <div className="zanzibar-hero__media" aria-hidden="true">
          <HeroSlideshow classPrefix="zanzibar-hero" slides={ZANZIBAR_HERO_SLIDES} fallback={IMAGE_FALLBACK} />
          <div className="zanzibar-hero__overlay" />
        </div>
        <div className="page-container zanzibar-hero__container">
          <div className="zanzibar-hero__content">
            <nav className="zanzibar-hero__breadcrumb" aria-label="Breadcrumb">
              <a href="/">Home</a>
              <span className="zanzibar-hero__breadcrumb-sep" aria-hidden="true">›</span>
              <a href="#tanzania-island">Island</a>
              <span className="zanzibar-hero__breadcrumb-sep" aria-hidden="true">›</span>
              <span>Zanzibar</span>
            </nav>
            <p className="zanzibar-hero__eyebrow">Tanzania Spice Islands</p>
            <h1 id="zanzibar-hero-heading">
              Zanzibar
              <span>Archipelago</span>
            </h1>
            <p className="zanzibar-hero__lead">
              Historic Stone Town, spice-scented farms, and Indian Ocean
              beaches the perfect finale after your mainland safari.
            </p>
            <div className="zanzibar-hero__actions">
              <a className="zanzibar-btn zanzibar-btn--primary" href="#contact">
                Plan your Zanzibar escape
              </a>
              <a className="zanzibar-btn zanzibar-btn--outline" href="#zanzibar-gallery">
                View gallery
              </a>
            </div>
          </div>
        </div>
        <div className="zanzibar-hero__stats">
          {HERO_STATS.map((stat) => (
            <div
              key={stat.label}
              className="zanzibar-hero__stat"
              style={{ '--stat-delay': `${stat.delay}ms` }}
            >
              <span className="zanzibar-hero__stat-value">{stat.value}</span>
              <span className="zanzibar-hero__stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section
        id="zanzibar-overview"
        className="zanzibar-section zanzibar-overview"
        aria-labelledby="overview-heading"
      >
        <div className="page-container">
          <div className="zanzibar-overview__grid">
            <Reveal className="zanzibar-overview__text">
              <p className="zanzibar-section__eyebrow">About the island</p>
              <h2 id="overview-heading">Where history meets the sea</h2>
              <p>
                Zanzibar is an archipelago off Tanzania&apos;s coast Unguja
                (often called Zanzibar Island) and Pemba shaped by centuries
                of Arab, Persian, Indian, and African trade. Stone Town&apos;s
                winding streets and carved doors tell stories of sultans, spices,
                and explorers.
              </p>
              <p>
                Beyond the city, palm-fringed beaches, coral reefs, and spice
                plantations offer a slower rhythm. Most travellers pair a few
                nights in Stone Town with beach time on the north or east coast
                the classic safari-and-sea combination that makes Tanzania
                unforgettable.
              </p>
              <ul className="zanzibar-overview__details">
                <Reveal as="li" className="zanzibar-overview__detail" delay={0}>
                  <span className="zanzibar-overview__detail-label">Location</span>
                  <span className="zanzibar-overview__detail-value">Indian Ocean, Tanzania</span>
                </Reveal>
                <Reveal as="li" className="zanzibar-overview__detail" delay={80}>
                  <span className="zanzibar-overview__detail-label">Main island</span>
                  <span className="zanzibar-overview__detail-value">Unguja (Zanzibar)</span>
                </Reveal>
                <Reveal as="li" className="zanzibar-overview__detail" delay={160}>
                  <span className="zanzibar-overview__detail-label">Heritage</span>
                  <span className="zanzibar-overview__detail-value">UNESCO Stone Town</span>
                </Reveal>
                <Reveal as="li" className="zanzibar-overview__detail" delay={240}>
                  <span className="zanzibar-overview__detail-label">Best for</span>
                  <span className="zanzibar-overview__detail-value">Culture &amp; beaches</span>
                </Reveal>
              </ul>
            </Reveal>
            <Reveal className="zanzibar-overview__image" variant="right" delay={200}>
              <SafeImage
                className="zanzibar-overview__img"
                src={ZANZIBAR_IMAGES.overview}
                alt="Seaside view with palm trees and buildings in Zanzibar"
              />
              <span className="zanzibar-overview__image-accent" aria-hidden="true" />
            </Reveal>
          </div>
        </div>
      </section>

      <section
        id="zanzibar-packages"
        className="zanzibar-section zanzibar-packages"
        aria-labelledby="packages-heading"
      >
        <div className="page-container">
          <Reveal className="zanzibar-section__header">
            <p className="zanzibar-section__eyebrow">Island packages</p>
            <h2 id="packages-heading">Zanzibar packages</h2>
            <p className="zanzibar-section__intro">
              From a Stone Town city break to a full safari-and-sea journey
              each package is tailored to your dates, lodge style, and pace.
            </p>
          </Reveal>
          <ul className="zanzibar-packages__grid">
            {SAFARI_PACKAGES.map((pkg, index) => (
              <Reveal
                key={pkg.title}
                as="li"
                className="zanzibar-packages__card"
                delay={index * 100}
              >
                <div className="zanzibar-packages__card-image">
                  <SafeImage src={pkg.image} alt={pkg.title} />
                  <span className="zanzibar-packages__duration">{pkg.duration}</span>
                </div>
                <div className="zanzibar-packages__card-body">
                  <span className="zanzibar-packages__tag">{pkg.tag}</span>
                  <h3>{pkg.title}</h3>
                  <p>{pkg.text}</p>
                  <ul className="zanzibar-packages__highlights">
                    {pkg.highlights.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <a className="zanzibar-packages__cta" href="#contact">
                    Enquire about this package →
                  </a>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="zanzibar-highlights"
        className="zanzibar-section zanzibar-wildlife"
        aria-labelledby="highlights-heading"
      >
        <div className="page-container">
          <Reveal className="zanzibar-section__header">
            <p className="zanzibar-section__eyebrow">Island highlights</p>
            <h2 id="highlights-heading">Discover Zanzibar</h2>
            <p className="zanzibar-section__intro">
              From Swahili heritage to reef-fringed shores six experiences
              that define the Spice Islands.
            </p>
          </Reveal>
          <ul className="zanzibar-wildlife__grid">
            {HIGHLIGHTS.map((item, index) => (
              <Reveal
                key={item.title}
                as="li"
                className="zanzibar-wildlife__card"
                delay={index * 100}
                variant="scale"
              >
                <div className="zanzibar-wildlife__icon-frame">
                  <span className="zanzibar-wildlife__icon-ring" aria-hidden="true" />
                  <span
                    className="zanzibar-wildlife__icon"
                    style={{ '--wildlife-face-position': item.facePosition }}
                  >
                    <img src={item.iconImage} alt={item.title} />
                  </span>
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="zanzibar-gallery"
        className="zanzibar-section zanzibar-gallery"
        aria-labelledby="gallery-heading"
      >
        <div className="page-container">
          <Reveal className="zanzibar-section__header">
            <p className="zanzibar-section__eyebrow">Photo gallery</p>
            <h2 id="gallery-heading">Scenes from Zanzibar</h2>
            <p className="zanzibar-section__intro">
              From Stone Town alleys to Indian Ocean horizons the island
              in every light.
            </p>
          </Reveal>
          <div className="zanzibar-gallery__grid">
            {GALLERY_IMAGES.map((img, index) => (
              <Reveal
                key={img.caption}
                className="zanzibar-gallery__item"
                delay={index * 100}
                variant="scale"
                onClick={() => setLightboxImage(img)}
                role="button"
                tabIndex={0}
                aria-label={`View ${img.caption}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setLightboxImage(img);
                  }
                }}
              >
                <SafeImage src={img.src} alt={img.caption} />
                <span className="zanzibar-gallery__caption">{img.caption}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {lightboxImage && (
        <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      )}

      <section
        id="zanzibar-seasons"
        className="zanzibar-section zanzibar-seasons"
        aria-labelledby="seasons-heading"
      >
        <div className="page-container">
          <Reveal className="zanzibar-section__header">
            <p className="zanzibar-section__eyebrow">Plan your timing</p>
            <h2 id="seasons-heading">Best time to visit Zanzibar</h2>
            <p className="zanzibar-section__intro">
              Warm water year-round choose your season for sun-soaked beaches,
              spice-farm greenery, or quieter shores.
            </p>
          </Reveal>
          <ul className="zanzibar-seasons__grid">
            {SEASONS.map((season, index) => (
              <Reveal
                key={season.title}
                as="li"
                className="zanzibar-seasons__card"
                delay={index * 120}
              >
                <span
                  className={`zanzibar-seasons__card-badge zanzibar-seasons__card-badge--${season.badge}`}
                >
                  {season.badge === 'peak'
                    ? '★ Peak Season'
                    : season.badge === 'green'
                      ? '🌿 Green Season'
                      : '◐ Shoulder Season'}
                </span>
                <h3>{season.title}</h3>
                <span className="zanzibar-seasons__card-months">{season.months}</span>
                <p>{season.text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="zanzibar-experiences"
        className="zanzibar-section zanzibar-experiences"
        aria-labelledby="experiences-heading"
      >
        <div className="page-container">
          <Reveal className="zanzibar-section__header">
            <p className="zanzibar-section__eyebrow">Island experiences</p>
            <h2 id="experiences-heading">Ways to explore Zanzibar</h2>
            <p className="zanzibar-section__intro">
              From heritage walks to reef days weave culture, spice, and
              beach time into your perfect island itinerary.
            </p>
          </Reveal>
          <ul className="zanzibar-experiences__grid">
            {EXPERIENCES.map((exp, index) => (
              <Reveal
                key={exp.title}
                as="li"
                className="zanzibar-experiences__card"
                delay={index * 120}
              >
                <div className="zanzibar-experiences__card-image">
                  <SafeImage src={exp.image} alt={exp.title} />
                </div>
                <div className="zanzibar-experiences__card-body">
                  <h3>{exp.title}</h3>
                  <p>{exp.text}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="zanzibar-getting-there"
        className="zanzibar-section zanzibar-getting-there"
        aria-labelledby="getting-there-heading"
      >
        <div className="page-container">
          <div className="zanzibar-getting-there__inner">
            <Reveal className="zanzibar-getting-there__copy">
              <p className="zanzibar-section__eyebrow">Getting there</p>
              <h2 id="getting-there-heading">How to reach Zanzibar</h2>
              <p>
                Zanzibar is well connected to mainland Tanzania by air and
                sea. Most safari itineraries end with a short flight from
                Arusha or Dar es Salaam to the island.
              </p>
              <ul className="zanzibar-getting-there__routes">
                {GETTING_THERE_ROUTES.map((route) => (
                  <li key={route}>{route}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal className="zanzibar-getting-there__map" variant="right" delay={200}>
              <iframe
                title="Zanzibar location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d255168.089654321!2d39.199!3d-6.165!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x185c0cb86477f173%3A0x9b0f7a0b0b0b0b0b!2sZanzibar!5e0!3m2!1sen!2stz!4v1700000000000!5m2!1sen!2stz"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="zanzibar-cta" aria-labelledby="zanzibar-cta-heading">
        <div className="page-container">
          <Reveal variant="scale" delay={0}>
            <div className="zanzibar-cta__inner">
              <h2 id="zanzibar-cta-heading">
                Ready for spice, sea, and Stone Town?
              </h2>
              <p>
                Tell us your travel dates we&apos;ll design a tailor-made
                Zanzibar escape with heritage walks, spice tours, and
                hand-picked beach lodges.
              </p>
              <a
                className="zanzibar-btn zanzibar-btn--primary zanzibar-btn--lg"
                href="#contact"
              >
                Get a free Zanzibar quote
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

export default Zanzibar;
